// Entry point: init Rapier WASM, show splash + home + map-select, then run
// a match. After the match ends, Back-to-Lobby tears down the game layer
// and re-shows the home screen for another match.

import { inject } from '@vercel/analytics';
import RAPIER from '@dimforge/rapier3d-compat';
import { CONFIG } from './config';
import { GameLoop } from './core/GameLoop';
import { Input } from './core/Input';
import { LocalTransport } from './net/LocalTransport';
import { ColyseusTransport } from './net/ColyseusTransport';
import { mountProfilerHud } from './ui/ProfilerHud';
import { isDevMode } from './ui/devMode';
import { REGIONS, getRegionById, getDefaultRegion, type Region } from './net/regions';
import { loadPreferredRegion, savePreferredRegion } from './ui/regionSettings';
import { showLobbyUnavailableModal } from './ui/online/LobbyUnavailableModal';
import { Client as ColyseusClient, type Room } from '@colyseus/sdk';
import { MapVoteScreen, type GoPayload } from './ui/MapVoteScreen';
import { OnlineFlowController } from './ui/online/OnlineFlowController';
import { showConnectingModal } from './ui/ConnectingModal';
import { PingHud } from './ui/PingHud';
import { CharacterView } from './render/CharacterView';
import { Renderer } from './render/Renderer';
import { preloadSpaceshipAssets } from './render/SpaceshipAssets';
import { preloadWoodenBoatAssets } from './render/WoodenBoatAssets';
import {
  preloadBundle, registerTransientBundle, unregisterTransientBundle, TRANSIENT_BUNDLE_ID
} from './maps/registry';
import { FxBus } from './fx/FxBus';
import { Hud } from './ui/Hud';
import { SpectatePanel } from './ui/SpectatePanel';
import { DebugPanel } from './ui/DebugPanel';
import { HomeScreen, randomName, PRESET_COLORS } from './ui/HomeScreen';
import { MapSelectScreen } from './ui/MapSelectScreen';
import { MapEditorScreen } from './ui/MapEditorScreen';
import { menuAudio } from './ui/MenuAudio';
import { SplashScreen } from './ui/SplashScreen';
import { paletteFromBase } from './ui/palette';
import { buildLocalRoster } from './sim/MatchState';
import type { TeamMember, MatchPhase } from './sim/MatchState';
import { NameTags } from './ui/NameTags';
import { showGameEndModal, showRoundEndModal } from './ui/RoundEndModal';
import { showFightBanner, playGameEndSfx } from './ui/RoundStartBanner';
import { loadGraphicsSettings, applyGraphicsSettingsToConfig, applyUserIblAfterInit } from './ui/graphicsSettings';
import { loadAudioSettings, applyAudioSettings } from './ui/audioSettings';
import { loadControlsSettings, applyControlsSettings } from './ui/controlsSettings';
import type { HatId } from './render/HatModels';
import type { MapJSON } from './maps/MapJSON';
import { t, setLocale, getStoredLocale, detectBrowserLocale, getLocale, onLocaleChange, clearStoredLocale, SUPPORTED_LANGUAGES } from './i18n';
import { LanguageSelectScreen } from './ui/LanguageSelectScreen';
import { showNamePickerModal } from './ui/NamePickerModal';
import { accountState } from './profile/account';
import { preloader } from './assets/Preloader';
import { mountPreloadDebugPanel } from './ui/PreloadDebugPanel';
import {
  shouldRunTutorial, saveTutorialState, resetTutorialState
} from './ui/tutorialState';
import type { TutorialFlowBehavior } from './maps/bundles/tutorial/behaviors/tutorialFlow';
import { NarratorEngine } from './maps/bundles/tutorial/narration/NarratorEngine';
import { resolveSubtitle } from './maps/bundles/tutorial/narration/subtitles';
import { NarratorCoordinator } from './maps/bundles/tutorial/narration/NarratorCoordinator';
import { mountSubtitleOverlay } from './ui/SubtitleOverlay';
import { TutorialHintBanner } from './ui/TutorialHintBanner';
import { LowFpsAdvisor } from './ui/LowFpsAdvisor';
import { PauseController } from './ui/PauseController';
import { PauseHintBanner } from './ui/PauseHintBanner';

interface JoinConfigSummary {
  name: string;
  hatId: HatId | 'none';
  color: string;
}

interface JoinIntent {
  /** Region id from the configured REGIONS table. Validated at parse time. */
  regionId: string;
  /** 5-character join code, uppercased + trimmed. */
  joinCode: string;
}

/** Parse a `?join=<region>-<code>` URL parameter into a JoinIntent.
 *  Returns null for any malformed input, missing region, or
 *  unconfigured region id — caller falls back to the standard boot
 *  (no auto-join, regular home screen). */
function parseJoinIntent(raw: string | null): JoinIntent | null {
  if (!raw) return null;
  // Find the LAST hyphen so multi-hyphen region ids ('us-east', 'us-west')
  // parse correctly: "us-east-ABC12" → region "us-east", code "ABC12".
  const dash = raw.lastIndexOf('-');
  if (dash <= 0 || dash >= raw.length - 1) return null;
  const regionId = raw.slice(0, dash).trim().toLowerCase();
  const joinCode = raw.slice(dash + 1).trim().toUpperCase();
  if (joinCode.length === 0 || joinCode.length > 16) return null;
  if (!/^[A-Z0-9]+$/.test(joinCode)) return null;
  if (!REGIONS.some((r) => r.id === regionId)) return null;
  return { regionId, joinCode };
}

/** Cap on chained play-again regroups. Practical users will never hit this;
 *  it's a guardrail against a programming bug spinning the recursion. */
const MAX_PLAY_AGAIN_DEPTH = 10;

/** Per-map asset preloader shared between local map-select and the online
 *  vote screen's transition. Each branch is idempotent / cached, so calling
 *  it twice is cheap. */
async function preloadMatchAssets(mapId: string): Promise<void> {
  if (mapId === 'spaceshipInRings') await preloadSpaceshipAssets();
  if (mapId === 'sinkingShip') await preloadWoodenBoatAssets();
  await preloadBundle(mapId);
}

/** All long-lived state of a live online match. Returned by
 *  prepareMatchInfrastructure() during the vote-screen morph so the
 *  caller can wait for endPromise + tear down once the match completes.
 *  The `loop` is already running by the time these handles are returned —
 *  the renderer is painting frames behind whatever overlay is still up,
 *  but the server's sim is frozen until match:ready is sent. */
interface MatchHandles {
  colyseus: ColyseusClient;
  gameRoom: Awaited<ReturnType<ColyseusClient['consumeSeatReservation']>>;
  transport: ColyseusTransport;
  renderer: Renderer;
  canvas: HTMLCanvasElement;
  input: Input;
  pause: PauseController;
  pauseHint: PauseHintBanner;
  fx: FxBus;
  hud: Hud;
  spectatePanel: SpectatePanel;
  lowFpsAdvisor: LowFpsAdvisor;
  nameTags: NameTags;
  pingHud: PingHud;
  loop: GameLoop;
  welcome: { charId: number };
  mapId: string;
  endPromise: Promise<void>;
  modals: { round: { dismiss(): void } | null; game: ReturnType<typeof showGameEndModal> | null };
  playAgainState: {
    reservation: { current: unknown | null };
    deadline: { current: number };
    timerInterval: { current: ReturnType<typeof setInterval> | null };
  };
  go: GoPayload;
}

/** Build the entire client-side match infrastructure: consume the seat
 *  reservation, preload assets, build canvas/transport/renderer, seed-render
 *  one frame, build fx/hud/nameTags/loop, wire play-again message handlers,
 *  and start the GameLoop.
 *
 *  Returned with the loop already running so the renderer is actively
 *  painting frames. The server's sim stays frozen (state.matchStarted=false)
 *  until every human sends `match:ready` — the caller is responsible for
 *  sending that signal AFTER the vote-screen wipe completes. */
async function prepareMatchInfrastructure(
  colyseus: ColyseusClient,
  go: GoPayload,
  homeToDispose: HomeScreen
): Promise<MatchHandles> {
  // Dispose the home behind the still-visible vote screen so the canvas
  // we're about to mount has a clean stage to paint into. The vote
  // screen at z:99 keeps covering everything until its wipe completes.
  homeToDispose.dispose();

  const gameRoom = await colyseus.consumeSeatReservation(go.reservation as never);
  installEarlyMessageSinks(gameRoom);

  // Welcome message carries the local charId; followed by a wait until
  // state.players is populated so the transport sees the full roster.
  const welcome = await new Promise<{ charId: number }>((resolve, reject) => {
    let resolved = false;
    gameRoom.onMessage('welcome', (msg: { charId: number }) => {
      if (resolved) return;
      resolved = true;
      resolve(msg);
    });
    gameRoom.onError((code, message) => reject(new Error(`game room error ${code}: ${message}`)));
    setTimeout(() => { if (!resolved) reject(new Error('welcome timeout')); }, 10_000);
  });
  await waitForState(
    gameRoom as never,
    (s: { players?: unknown[] }) => Array.isArray(s.players) && s.players.length === 4,
    5_000
  );

  const mapId = (gameRoom.state as { mapId: string }).mapId || go.mapId;
  await preloadMatchAssets(mapId);

  const app = document.getElementById('app')!;
  const canvas = document.createElement('canvas');
  app.appendChild(canvas);

  const input = new Input(canvas);
  const transport = new ColyseusTransport(input, {
    room: gameRoom as never,
    localCharId: welcome.charId,
    mapId
  });
  const renderer = new Renderer(canvas, mapId, transport.world.arena);
  await renderer.init();
  applyUserIblAfterInit(renderer);
  await renderer.awaitAssetsReady();
  renderer.setSimWorld(transport.world);

  for (const m of transport.match.members) {
    const palette = paletteFromBase(m.color);
    renderer.addCharacter(m.id, new CharacterView(palette, { hatId: m.hatId }));
  }

  // Seed one frame so the canvas has something to paint behind the
  // still-visible vote-screen overlay.
  transport.tick(1 / 60);
  const seedPrev = transport.prevSnapshot();
  const seedCurr = transport.latestSnapshot();
  renderer.render(seedPrev, seedCurr, 1, 0, 1, input.cameraYaw(), input.cameraPitch(), transport.match.focusId);

  const fx = new FxBus(renderer, welcome.charId);
  const hud = new Hud({ match: transport.match });
  const spectatePanel = new SpectatePanel();
  const lowFpsAdvisor = new LowFpsAdvisor();
  const nameTags = new NameTags(renderer, transport.match);
  const pingHud = new PingHud(transport.match, transport);
  // Netcode profiler HUD — only mounted in dev sessions (localhost or
  // ?dev=1). F9 toggles visibility once mounted.
  if (isDevMode()) {
    const profilerHud = mountProfilerHud({ transport, mapId });
    void profilerHud;
  }

  // Match-running state shared between GameLoop callbacks and the post-
  // match teardown path. Boxed so the closures can mutate freely.
  const modals: MatchHandles['modals'] = { round: null, game: null };
  let resolveEnd: (() => void) | null = null;
  const endPromise = new Promise<void>((res) => { resolveEnd = res; });
  const playAgainState: MatchHandles['playAgainState'] = {
    reservation: { current: null },
    deadline: { current: 0 },
    timerInterval: { current: null }
  };

  // Play-again wiring — server emits `playAgain:open` (window starts),
  // `playAgain:closed` (resolved), and `playAgain:reservation` (only to
  // consenters when a regroup forms).
  gameRoom.onMessage('playAgain:open', (msg: { deadline: number; windowMs?: number }) => {
    // Derive the deadline from the *duration* and the local clock — the
    // server's absolute deadline timestamp would be susceptible to any
    // wall-clock skew between Node and the browser (which we hit in
    // practice — the countdown was rendering as 99s indefinitely). The
    // server still sends `windowMs` alongside `deadline`; prefer it when
    // present. Fall back to PLAY_AGAIN_WINDOW_MS only if the server
    // payload is malformed.
    const windowMs = (Number.isFinite(msg.windowMs) && msg.windowMs! > 0)
      ? msg.windowMs!
      : 15_000;
    playAgainState.deadline.current = Date.now() + windowMs;
    if (playAgainState.timerInterval.current) clearInterval(playAgainState.timerInterval.current);
    playAgainState.timerInterval.current = setInterval(() => {
      if (!modals.game) return;
      const deadline = playAgainState.deadline.current;
      if (!Number.isFinite(deadline) || deadline <= 0) return;
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      modals.game.setPlayAgainSeconds(remaining);
      if (remaining === 0 && playAgainState.timerInterval.current) {
        clearInterval(playAgainState.timerInterval.current);
        playAgainState.timerInterval.current = null;
      }
    }, 250);
  });
  gameRoom.onMessage('playAgain:closed', (msg: { regrouped: boolean }) => {
    if (playAgainState.timerInterval.current) {
      clearInterval(playAgainState.timerInterval.current);
      playAgainState.timerInterval.current = null;
    }
    modals.game?.setPlayAgainClosed(msg.regrouped);
  });
  gameRoom.onMessage('playAgain:reservation', (msg: { reservation: unknown }) => {
    playAgainState.reservation.current = msg.reservation;
    resolveEnd?.();
  });

  // GameLoop — same shape as the original runOnlineMatchFromGo body. The
  // step callback advances transport state + drives modals; the render
  // callback paints. Starts immediately so the canvas is animating
  // (or at least painting the spawn pose) BEFORE the vote-screen wipe
  // reveals it. Server's matchStarted gate keeps the sim frozen so the
  // local player won't see AI mid-fight on first frame.
  const BASE_SATURATION = CONFIG.render.saturation;
  const REPLAY_SATURATION = 0.55;
  const REPLAY_BLUR_MAX_PX = 12;
  let lastAppliedBlurPx = 0;
  let prevPhase: MatchPhase | '' = '';

  const pauseHint = new PauseHintBanner(input);

  const loop = new GameLoop(
    (dt) => {
      transport.tick(dt);

      const localId = transport.match.localPlayer().id;
      const localCh = transport.latestSnapshot()?.characters.find((c) => c.id === localId);
      const localDead = !!localCh && (localCh.wasted || localCh.ringedOut);
      input.setSpectateVisible(localDead && transport.match.phase === 'playing');
      input.setLocalMidAir(!!localCh && localCh.bothAir);
      const l = input.consumeSpectateLeft();
      const r = input.consumeSpectateRight();
      spectatePanel.syncFrom(input, transport.match, localDead);
      if (transport.match.phase === 'playing' && localDead) {
        if (l > 0) spectatePanel.flash('left');
        if (r > 0) spectatePanel.flash('right');
        for (let i = 0; i < l; i++) transport.cycleFocus(-1);
        for (let i = 0; i < r; i++) transport.cycleFocus(+1);
      }

      const phase = transport.match.phase;
      if (phase !== prevPhase) {
        if (phase === 'playing' && prevPhase !== 'playing') {
          showFightBanner();
        } else if (phase === 'roundOver') {
          modals.round = showRoundEndModal(transport.match, {
            onContinue: () => { modals.round = null; }
          });
        } else if (phase === 'gameOver') {
          playGameEndSfx(transport.match.lastRoundWinner === transport.match.localTeam());
          // Suppress the Play Again button if the local player is the only
          // human still connected — the server requires ≥2 humans to
          // regroup, so the button would just age out and confuse the user.
          const players = (gameRoom.state as { players?: Array<{ connected: boolean; isAI: boolean }> }).players ?? [];
          let connectedHumans = 0;
          for (const p of players) if (p && p.connected && !p.isAI) connectedHumans += 1;
          const canRegroup = connectedHumans >= 2;
          modals.game = showGameEndModal(transport.match, {
            onBackToLobby: () => {
              modals.game = null;
              resolveEnd?.();
            },
            onPlayAgain: canRegroup
              ? () => { try { gameRoom.send('playAgain', { yes: true }); } catch { /* noop */ } }
              : undefined,
            showPlayAgainTimer: canRegroup
          });
        }
        prevPhase = phase;
      }
    },
    (alpha, realDt) => {
      transport.renderTick(realDt * 1000);
      transport.pollAndSendLiveInput(realDt);

      const liveSnap = transport.latestSnapshot();
      if (liveSnap) fx.koReplay.armIfNeeded(liveSnap);
      fx.koReplay.step(realDt, liveSnap ? liveSnap.tick : 0);

      let renderPrev = transport.prevSnapshot();
      let renderCurr = liveSnap;
      let renderAlpha = alpha;
      if (fx.koReplay.active() && liveSnap) {
        const headF = fx.koReplay.headTick();
        const ti = Math.floor(headF);
        renderCurr = transport.snapshotAtTick(ti) ?? liveSnap;
        renderPrev = transport.snapshotAtTick(ti - 1) ?? renderPrev;
        renderAlpha = headF - ti;
      }

      if (renderCurr) {
        fx.step(realDt, renderCurr);
        hud.update(renderCurr);
        nameTags.update(renderCurr);
      }

      const desat = fx.koReplay.desatAmount();
      renderer.post.setSaturation(BASE_SATURATION + (REPLAY_SATURATION - BASE_SATURATION) * desat);

      const blurPx = fx.koReplay.blurAmount() * REPLAY_BLUR_MAX_PX;
      const quantized = Math.round(blurPx * 10) / 10;
      if (quantized !== lastAppliedBlurPx) {
        canvas.style.filter = quantized > 0 ? `blur(${quantized}px)` : '';
        lastAppliedBlurPx = quantized;
      }

      hud.tickFps(realDt);
      pingHud.update();
      pauseHint.tick();
      // Live remote bones bypass the prev/curr alpha-lerp for remotes — that
      // path can't add sub-step motion to bracket-interpolated wire data.
      // KO replay scrubs through historical snapshots (snapshotAtTick), so
      // disable the live path while it's active and let the alpha-lerp drive
      // the scene from buffered scalar+bone snaps as before.
      const liveRemoteBones = fx.koReplay.active()
        ? undefined
        : (id: number) => transport.remoteBonesAt(id);
      renderer.render(
        renderPrev, renderCurr, renderAlpha, realDt,
        fx.timeScale(), input.cameraYaw(), input.cameraPitch(),
        transport.match.focusId, liveRemoteBones
      );
    }
  );
  loop.start();

  (window as any).debug = { transport, renderer, fx };

  // Pause / settings access during the online match. Esc, gamepad Start,
  // and the mobile ⋯ button all open the same modal. Suppressed while a
  // RoundEndModal / GameEndModal is up — those have their own Esc paths.
  const pause = new PauseController({
    input,
    isTutorial: false,
    isBlocked: () => modals.round !== null || modals.game !== null
  });
  pause.start();

  return {
    colyseus, gameRoom, transport, renderer, canvas, input, pause, pauseHint,
    fx, hud, spectatePanel, lowFpsAdvisor, nameTags, pingHud, loop, welcome, mapId,
    endPromise, modals, playAgainState, go
  };
}

/** Drive the prepared match through to completion: send match:ready
 *  (after which the server unfreezes the sim once every human has signalled),
 *  wait for matchStarted, await the end of the match, then teardown.
 *  Branches on play-again to either re-enter the online flow against a
 *  regroup PartyRoom or fall through to the standard back-to-lobby wipe. */
async function runMatchToCompletion(
  handles: MatchHandles,
  config: JoinConfigSummary,
  playAgainDepth: number
): Promise<void> {
  const {
    colyseus, gameRoom, transport, renderer, canvas, input, pause, pauseHint,
    fx, hud, spectatePanel, lowFpsAdvisor, nameTags, pingHud, loop, modals, playAgainState, endPromise
  } = handles;

  // Tell the server we're rendering live frames. Sim stays frozen on the
  // server until every human has signalled. The GameLoop is already
  // running on this client — once matchStarted flips true we'll start
  // receiving live snapshots and the scene animates.
  try { (gameRoom as { send: (t: string, p: unknown) => void }).send('match:ready', {}); } catch { /* noop */ }
  await waitForState(
    gameRoom as never,
    (s: { matchStarted?: boolean }) => s.matchStarted === true,
    20_000
  );

  void menuAudio.fadeMusic(0, 700);
  // Vote-screen wipe has finished and the live game is on-screen — kick
  // off the map's music now (FxBus deferred playback at construction).
  fx.startMusic();

  await endPromise;

  if (playAgainState.timerInterval.current) {
    clearInterval(playAgainState.timerInterval.current);
    playAgainState.timerInterval.current = null;
  }

  // Leave the room first so the server disposes our slot. If the server
  // already tore the room down (e.g. play-again window timed out and the
  // server hit `this.disconnect()` 2 s later) the WebSocket is already
  // closed — `leave()` then either rejects or hangs forever waiting for
  // a server ack that will never come, which strands the user on the
  // game-end modal. Race with a short timeout so back-to-lobby can't get
  // stuck regardless of socket state.
  await Promise.race([
    gameRoom.leave().catch(() => undefined),
    new Promise<void>((resolve) => setTimeout(resolve, 800))
  ]);
  transport.dispose();
  pingHud.dispose();

  if (playAgainState.reservation.current !== null) {
    // Play-again branch — tear down match layer immediately and chain
    // into a fresh online flow against the regroup PartyRoom.
    loop.stop();
    pause.stop();
    pauseHint.dispose();
    fx.dispose();
    hud.dispose();
    spectatePanel.dispose();
    lowFpsAdvisor.dispose();
    nameTags.dispose();
    input.dispose();
    canvas.remove();
    document.getElementById('match-modals')?.remove();
    renderer.disposeLifecycle(); try { renderer.renderer.dispose?.(); } catch { /* noop */ }
    delete (window as any).debug;
    if (playAgainDepth >= MAX_PLAY_AGAIN_DEPTH) {
      console.warn(`[runOnlineMatch] play-again depth cap (${MAX_PLAY_AGAIN_DEPTH}) reached — returning home`);
      const home = prepareReturnHome();
      home.root.style.zIndex = '';
      home.activateNav();
      (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = home;
      return;
    }
    let regroupRoom: Awaited<ReturnType<typeof colyseus.consumeSeatReservation>>;
    try {
      regroupRoom = await colyseus.consumeSeatReservation(playAgainState.reservation.current as never);
    } catch (err) {
      console.error('[runOnlineMatch] play-again consumeSeatReservation failed', err);
      const home = prepareReturnHome();
      home.root.style.zIndex = '';
      home.activateNav();
      (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = home;
      return;
    }
    const home = new HomeScreen();
    home.activateNav();
    home.enterOnlineMode();
    await continueOnlineMatchAfterRegroup(colyseus, regroupRoom as never, home, config, playAgainDepth + 1);
    return;
  }

  // Standard return path — wipe the game layer to reveal a fresh home.
  const home = prepareReturnHome();
  const swipeMs = 700;
  const gameWipeEls = [
    document.getElementById('app'),
    document.getElementById('hud'),
    document.getElementById('match-modals')
  ].filter((el): el is HTMLElement => el !== null);
  const originalGameZ = gameWipeEls.map((el) => el.style.zIndex);
  for (const el of gameWipeEls) {
    const currentZ = parseInt(el.style.zIndex || '', 10);
    if (!Number.isFinite(currentZ) || currentZ < 50) el.style.zIndex = '50';
  }
  const audioFade = Promise.all([fx.fadeAndStopAudio(swipeMs), fadeOutDomAudio(swipeMs)]);
  menuAudio.fadeMusic(1.0, swipeMs);
  await wipeGameLayerOut(gameWipeEls, swipeMs);
  await audioFade;
  modals.round?.dismiss();
  modals.game?.dismiss();
  loop.stop();
  pause.stop();
  pauseHint.dispose();
  fx.dispose();
  hud.dispose();
  spectatePanel.dispose();
  lowFpsAdvisor.dispose();
  nameTags.dispose();
  input.dispose();
  canvas.remove();
  document.getElementById('match-modals')?.remove();
  renderer.disposeLifecycle(); try { renderer.renderer.dispose?.(); } catch { /* noop */ }
  delete (window as any).debug;
  for (let i = 0; i < gameWipeEls.length; i++) {
    const el = gameWipeEls[i]!;
    el.style.clipPath = '';
    el.style.transition = '';
    el.style.zIndex = originalGameZ[i] ?? '';
  }
  home.root.style.zIndex = '';
  home.activateNav();
  (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = home;
}

/** Block boot until we have a server-validated account. If the local token
 *  resolves, we proceed silently. Otherwise we show the name picker as a
 *  modal until the user successfully registers. Network failures degrade
 *  gracefully — we keep retrying inside the modal, but if the user dismisses
 *  via Esc (only allowed in rename mode, which this isn't) we'd be stuck.
 *  In register mode the modal has no cancel button, so this is a hard gate. */
async function ensureAccount(): Promise<void> {
  try {
    const existing = await accountState.loadFromStorage();
    if (existing) return;
  } catch (e) {
    console.warn('[boot] account load failed', e);
  }
  // No (valid) token → show the picker. We pre-fill from the legacy local
  // profile name if it's available and likely to pass validation, so a
  // returning offline player gets a one-tap onboarding.
  let initialName: string | undefined;
  try {
    const raw = localStorage.getItem('games:homeProfile');
    if (raw) {
      const parsed = JSON.parse(raw) as { name?: unknown };
      if (typeof parsed.name === 'string' && /^[a-zA-Z0-9_-]{3,16}$/.test(parsed.name)) {
        initialName = parsed.name;
      }
    }
  } catch {}
  await new Promise<void>((resolve) => {
    showNamePickerModal({
      mode: 'register',
      initialName,
      onSubmit: async (name) => {
        await accountState.register(name);
        resolve();
      },
    });
  });
}

async function main(): Promise<void> {
  // Initialize Vercel Web Analytics
  inject();

  // QA reset hook: clears the tutorial-completion flag and reloads. Set
  // unconditionally before any UI exists so it's reachable from devtools at
  // every point of the boot sequence.
  (window as unknown as { __resetTutorial?: () => void }).__resetTutorial = () => {
    resetTutorialState();
    location.reload();
  };

  const queryParams = new URLSearchParams(location.search);

  // Mark the document so CSS can branch on dev sessions (e.g. the static
  // .controls-hint cheatsheet in index.html only appears when this flag
  // is set). isDevMode() reads ?dev=1.
  if (isDevMode()) document.documentElement.setAttribute('data-dev', '1');

  // Preload debug overlay: shown for the entire session whenever the URL
  // contains ?debugPreload=1. No env var, no toggle — just the query param.
  if (queryParams.has('debugPreload')) {
    mountPreloadDebugPanel();
  }

  // Join-link parsing. Format: `?join=<regionId>-<joinCode>`. RegionId is
  // validated against the configured regions; joinCode is uppercased and
  // bounded to a sane length. An invalid value (unknown region, malformed
  // code) just leaves joinIntent unset — the user lands on the normal home
  // screen as if the param wasn't there. The "lobby unavailable" modal
  // handles the case where a parsed link points to a party that no longer
  // exists. `let` so the outer loop can clear it after the first iteration
  // (we only auto-join once per boot).
  let joinIntent = parseJoinIntent(queryParams.get('join'));

  // Language debug params:
  //   ?resetLang     — wipe the stored locale so the picker reappears on
  //                    boot. Use to verify auto-detection on a return visit.
  //   ?lang=xx       — force the picker to open with `xx` pre-selected
  //                    (and supersede whatever the browser would auto-
  //                    detect). Used to confirm the pre-selection +
  //                    auto-scroll work for languages the dev's browser
  //                    doesn't speak. Validated against SUPPORTED_LANGUAGES;
  //                    unknown codes are ignored.
  if (queryParams.has('resetLang')) clearStoredLocale();
  const langParamRaw = queryParams.get('lang')?.toLowerCase().trim() ?? null;
  const debugLangCode = (langParamRaw && SUPPORTED_LANGUAGES.some((l) => l.code === langParamRaw))
    ? langParamRaw
    : null;

  // Initialise locale to the stored value (return visit) or the closest
  // browser-language match, so any text the language screen renders
  // (title + Continue label) starts in the user's language. ?lang=xx
  // overrides everything for the debug path.
  const storedLocale = getStoredLocale();
  setLocale(debugLangCode ?? storedLocale ?? detectBrowserLocale());

  // Apply persisted user settings to CONFIG before any consumer reads
  // them. Graphics → CONFIG.render before any Renderer is built; audio →
  // CONFIG.audio + every live AudioBus (the menu bus is already
  // constructed at module import); controls → CONFIG.controls so the
  // first mouse-move event honours the saved sensitivity / invert-Y.
  applyGraphicsSettingsToConfig(loadGraphicsSettings());
  applyAudioSettings(loadAudioSettings());
  applyControlsSettings(loadControlsSettings());

  const boot = document.getElementById('boot');
  if (boot) {
    boot.classList.add('hidden');
    setTimeout(() => boot.remove(), 600);
  }

  // Language + preload gate. The first user interaction on this screen
  // resumes the AudioContext (browsers require user activation) and kicks
  // off the tiered asset preload. Continue blocks only on tier 1
  // (splash-critical: music-home + logo + whoosh) so the splash's
  // perfectly-timed sequence is guaranteed to land on cached assets.
  // Rapier WASM init runs in parallel with the screen so it's done by
  // the time the user picks. The screen is normally always shown — even
  // on return visits with a stored locale — because it doubles as the
  // preload / audio-unlock gate. ?lang=xx overrides the pre-selected
  // flag for debugging the auto-detect + auto-scroll path.
  //
  // EXCEPTION (and ONLY this exception): when arriving via a `?join=...`
  // link AND a locale is already cached. In that one case we skip the
  // picker so the user flows straight through to the lobby they're
  // trying to join. EVERY other entry path — fresh visit, refresh on
  // home, debug-language flag, missing cached locale — still shows the
  // picker so the audio-unlock + preload-start interaction happens.
  const skipLangPicker = (
    joinIntent !== null
    && storedLocale !== null
    && debugLangCode === null
  );
  if (skipLangPicker) {
    preloader.startAll();
    await Promise.all([
      preloader.awaitTier(1),
      RAPIER.init()
    ]);
  } else {
    const langScreen = new LanguageSelectScreen({
      initialCode: debugLangCode ?? storedLocale ?? detectBrowserLocale()
    });
    const [pickedLocale] = await Promise.all([
      langScreen.show(),
      RAPIER.init()
    ]);
    setLocale(pickedLocale);
    langScreen.dispose();
  }
  // Tiers 2-5 keep loading in the background through splash + home.
  void preloader.awaitTier(2);

  // Soft-auth: load (or create) the player's account before anything that
  // depends on identity. We block here so the home screen always has an
  // accountState.get() to work with. Network failures fall through silently
  // — the home screen handles a null account by hiding online-only UI.
  await ensureAccount();

  // Tutorial gate: first-time players play through the six teaching rooms
  // before the splash. Skipped-with-confirm or pit-completion both flip the
  // localStorage flag so the tutorial is never re-shown. QA reset is the
  // window.__resetTutorial() hook installed at the top of main().
  // Skip entirely when arriving via a join link — the user is trying to
  // join a friend's lobby, not start a tutorial.
  if (!joinIntent && shouldRunTutorial()) {
    await runTutorial();
  }

  // First-time flow: splash owns the continuous scroll column, home mounts
  // inside it, user picks a config, then map-select + game chain off it.
  const splash = new SplashScreen();
  await splash.setupPromise;
  const homeScreen = new HomeScreen();
  splash.mountHome(homeScreen.root);
  const configPromise = homeScreen.show();
  await splash.show();
  // Splash has dismissed — the home is now the interactive surface. Turn on
  // its menu nav (keyboard arrows, gamepad dpad, Enter/A) here rather than
  // in HomeScreen's constructor, so Arrow/Enter during splash stayed
  // hitting splash's own "press any key" handler.
  homeScreen.activateNav();
  // First-boot baseline: record current level so future level-ups have
  // something to compare against. Won't celebrate anything on this call.
  void homeScreen.runLevelUpCheckOnEnter();
  // When arriving via a join link, programmatically click the ONLINE menu
  // item so the user lands directly in the OnlineFlowController against
  // the linked party. Their default identity (saved profile / random
  // fallback) is used; they can still leave online play to return to the
  // home menu and adjust it.
  if (joinIntent) homeScreen.triggerOnlineFromJoinIntent();
  let config = (await configPromise) as JoinConfigSummary;
  splash.dispose();

  // Main loop: each iteration = one "thing" (a match OR the editor). "Back
  // to Lobby" on the game-end modal resolves runMatch; exiting the editor
  // resolves runEditor. Either way we re-show home for the next pick.
  // For online play, the home stays mounted through the party panel and
  // matchmaking phases; runOnlineMatch passes the same instance back via
  // _pendingReturnHome on a "Leave online play" exit, or disposes it
  // when a match actually launches (in which case prepareReturnHome
  // creates a fresh post-match home).
  let activeHome: HomeScreen | null = homeScreen;
  while (true) {
    const action = (config as { action?: 'play' | 'online' | 'editor' | 'leaderboard' }).action ?? 'play';
    if (action === 'editor') {
      activeHome = null;
      await runEditor();
    } else if (action === 'online') {
      const home = activeHome ?? new HomeScreen();
      activeHome = null;
      // The autoJoin payload only fires for the FIRST iteration of the
      // outer loop on this boot — once the user leaves online play once
      // we treat them as a "regular" online player and don't re-attempt
      // the link.
      const autoJoin = joinIntent;
      joinIntent = null;
      await runOnlineMatch(config, home, autoJoin ?? undefined);
    } else if (action === 'leaderboard') {
      activeHome = null;
      const { showLeaderboardScreen } = await import('./ui/LeaderboardScreen');
      await showLeaderboardScreen();
    } else {
      activeHome = null;
      await runMatch(config);
    }
    config = await showHomeStandalone();
    activeHome = (showHomeStandalone as unknown as { _lastReturnedHome?: HomeScreen })._lastReturnedHome ?? null;
  }
}

/** First-boot tutorial. Local-only; runs between language pick and splash.
 *
 *  Builds a stripped match scaffold (canvas + Input + LocalTransport +
 *  Renderer + GameLoop) for the `tutorial` map bundle, mounts a Skip
 *  button, and awaits the tutorialFlow behavior's `done` Promise — which
 *  resolves either when the player jumps in the room-6 pit (kind='completed')
 *  or when the Skip confirm is accepted (kind='skipped'). Both outcomes
 *  flip the persisted flag so the tutorial isn't shown again until reset.
 *
 *  Roster note: all four sim slots are on team 'red', so the World's
 *  team-aware round-end check (`teamsPresent.size >= 2`) never fires —
 *  the round stays in 'playing' phase indefinitely. Slots 2 and 3 spawn
 *  in a containment box at (1000, 0, 1000) per the map.json spawns; the
 *  passive AI never inputs anything so they just stand there. */
async function runTutorial(): Promise<void> {
  await preloadBundle('tutorial');

  const roster: TeamMember[] = [
    { id: 0, name: 'Player',   color: '#3DA6FF', hatId: null,    team: 'red', isLocal: true },
    { id: 1, name: 'Timmy',    color: '#FF6A2A', hatId: 'party', team: 'red', isLocal: false },
    { id: 2, name: '_extra1',  color: '#888888', hatId: null,    team: 'red', isLocal: false },
    { id: 3, name: '_extra2',  color: '#888888', hatId: null,    team: 'red', isLocal: false }
  ];

  const app = document.getElementById('app')!;
  const canvas = document.createElement('canvas');
  app.appendChild(canvas);

  // Tutorial-only graphics overrides: bloom and cinematic flares (anamorphic)
  // are forced off regardless of user settings. The PBR-textured rooms have
  // bright, broad surfaces that read poorly under bloom — and flares draw the
  // eye away from the lessons. Restore on teardown so the next match honours
  // the user's preferences again.
  const savedBloom = CONFIG.render.bloom;
  const savedAnamorphic = CONFIG.render.anamorphic;
  CONFIG.render.bloom = false;
  CONFIG.render.anamorphic = false;

  const input = new Input(canvas);
  // Align the follow-cam with the player's spawn yaw (+x facing). Default
  // Input yaw is 0 (camera looks down +z) which would land sideways relative
  // to the room rows; π/2 puts the camera behind the player at spawn.
  input.setCameraYaw(Math.PI / 2);
  const transport = new LocalTransport(input, {
    roster,
    mapId: 'tutorial',
    aiDifficulty: 'passive'
  });
  const renderer = new Renderer(canvas, 'tutorial', transport.world.arena);
  await renderer.init();
  applyUserIblAfterInit(renderer);
  await renderer.awaitAssetsReady();
  renderer.setSimWorld(transport.world);

  for (const m of roster) {
    const palette = paletteFromBase(m.color);
    renderer.addCharacter(m.id, new CharacterView(palette, { hatId: m.hatId }));
  }

  // Seed one frame so the canvas is painting before the GameLoop takes over.
  transport.tick(1 / 60);

  const flow = transport.world.arena.behaviorDefsById.get('tutorialFlow') as
    TutorialFlowBehavior | undefined;
  if (!flow) {
    throw new Error('[runTutorial] tutorialFlow behavior was not registered for the tutorial bundle');
  }

  // Locate the player + NPC characters for the narrator coordinator. The
  // sim's character roster is keyed on TeamMember.id; player is 0, NPC is 1.
  const playerChar = transport.world.characters.find(c => c.id === 0);
  const npcChar = transport.world.characters.find(c => c.id === 1);
  if (!playerChar || !npcChar) {
    throw new Error('[runTutorial] expected player + NPC character to be in the world after seed tick');
  }

  // FxBus drives footsteps, swing/hit one-shots, music, and the shared
  // reverb convolver. Construction reads the audio block from the bundle's
  // map.json (irUrl, wet, footsteps urls). Same call shape as runMatch.
  const fx = new FxBus(renderer, 0);

  // Narration / subtitles. Per-locale path with EN fallback. The narrator
  // routes through its own convolver loaded with the SAME IR file the
  // FxBus uses, so the voice sounds like it's in the same room as the
  // gameplay SFX. Wet level mirrors map.json's audio.reverb.wet.
  const subtitleOverlay = mountSubtitleOverlay();
  let locale = getLocale();
  const audioDef = renderer.arena.audio;
  // Route the narrator through the match's AudioBus so the user's
  // SFX volume (and master) slider applies to dialogue alongside every
  // other game sound. Voice still skips the bus's per-source underwater
  // lowpass — narration shouldn't muffle when the camera dips below
  // water.
  const narrator = new NarratorEngine({
    basePath: 'tutorial',
    locale,
    fallbackLocale: 'en',
    onSubtitleChange: (text) => subtitleOverlay.setText(text),
    resolveSubtitle: (key, loc) => resolveSubtitle(key, loc),
    // 80% of the previous 0.95 — the radio-band makeup gain (×1.4 inside
    // NarratorEngine) was nudging the dialogue above the music.
    volume: 0.76,
    reverbIrUrl: audioDef?.reverb?.irUrl ? `/${audioDef.reverb.irUrl}` : undefined,
    reverbWet: audioDef?.reverb?.wet ?? 1.0
  }, fx.audioBus);

  // Locale change mid-tutorial ��� feed the new code into the narrator so
  // any subsequent line uses the new locale's audio file + subtitle.
  // The currently-playing line finishes in its original language; we
  // don't yank it mid-sentence.
  const detachLocaleListener = onLocaleChange((next) => {
    locale = next;
    narrator.setLocale(next);
  });

  // Black overlay covering the scene while it warms up. Removed via opacity
  // ramp once the renderer has painted a few frames AND the music+narrator
  // are ready to take over the player's attention.
  const FADE_DURATION_MS = 3000;
  // Beat after the fade starts before the first narration line — gives the
  // player a moment to register where they are before the voice starts.
  const NARRATION_LEAD_MS = 1000;
  const blackEl = document.createElement('div');
  blackEl.style.cssText = [
    'position: fixed', 'inset: 0', 'z-index: 1400',
    'background: black', 'opacity: 1',
    `transition: opacity ${FADE_DURATION_MS}ms ease-out`,
    'pointer-events: none'
  ].join('; ');
  document.body.appendChild(blackEl);

  // Frame counter — flips to 'ready' once the renderer has painted enough
  // frames that texture loads / IBL bake / shader compile have settled.
  // Anything below ~10 frames at 60 Hz is the white "warmup" the user
  // wants to hide, so 20 frames is a comfortable margin.
  const FRAMES_BEFORE_READY = 20;
  let framesPainted = 0;
  let rendererReady = false;

  const coord = new NarratorCoordinator({
    narrator,
    fadeMusicToZero: () => { void fx.fadeAndStopAudio(700); },
    flow,
    player: playerChar,
    npc: npcChar
  });

  const hintBanner = new TutorialHintBanner({
    input,
    flow,
    coord,
    isDoubleHolding: () => {
      const snap = transport.latestSnapshot();
      const ch = snap?.characters.find((c) => c.id === 0);
      return !!ch && ch.doubleHolding;
    }
  });
  const lowFpsAdvisor = new LowFpsAdvisor();

  // Pause / settings during the tutorial. Esc, gamepad Start, and the
  // mobile ⋯ button open the same modal — and during the tutorial the
  // Gameplay tab also surfaces a Skip Tutorial action. Suppressed while
  // a narrator cinematic is running, so the user can't bypass the
  // asleep-then-awaken sequence or skip a phase intro.
  const pause = new PauseController({
    input,
    isTutorial: true,
    tutorialFlow: {
      requestSkip: () => {
        // Match the existing skipBtn behavior: silence the narrator first
        // so the modal doesn't dismiss to a still-talking voiceover.
        coord.abort();
        flow.requestSkip();
      }
    },
    isBlocked: () => coord.isCinematicActive()
  });
  pause.start();

  const loop = new GameLoop(
    (dt) => {
      transport.tick(dt);
      coord.tick(dt);
    },
    (alpha, realDt) => {
      const prev = transport.prevSnapshot();
      const curr = transport.latestSnapshot();
      renderer.render(
        prev, curr,
        alpha, realDt, 1.0,
        input.cameraYaw(), input.cameraPitch(),
        transport.match.focusId
      );
      // FxBus.step processes per-tick discrete events (impacts, footsteps,
      // swings) and drives the music ramp. Snapshot is non-null after the
      // first transport.tick — we seed one frame above before the loop
      // starts, so this is safe.
      if (curr) fx.step(realDt, curr);

      // Mobile button label flips: PUNCH → THROW while double-handing,
      // HEADBUTT → KICK while airborne. Both are no-ops on KBM/gamepad.
      const localCh = curr?.characters.find((c) => c.id === 0);
      input.setLocalMidAir(!!localCh && localCh.bothAir);
      input.setLocalDoubleHolding(!!localCh && localCh.doubleHolding);

      hintBanner.tick();
      if (!rendererReady) {
        framesPainted += 1;
        if (framesPainted >= FRAMES_BEFORE_READY) {
          rendererReady = true;
          // Fade-from-black + music start on the same frame; narration
          // intentionally leads in 1 s LATER so the voice doesn't compete
          // with the visual wake-up.
          blackEl.style.opacity = '0';
          fx.startMusic();
          setTimeout(() => coord.beginIntro(), NARRATION_LEAD_MS);
          setTimeout(() => blackEl.remove(), FADE_DURATION_MS + 100);
        }
      }
    }
  );
  loop.start();
  transport.start();

  const outcome = await flow.done;

  // Fade-to-black overlay covers the canvas teardown and the splash mount.
  const fadeEl = mountFadeToBlackOverlay(600);
  // If the player jumped into the pit while the narrator was still talking,
  // the coordinator already pumps music down. Either way pull voice + music
  // to silence so the splash stars-fade-in isn't talked over.
  void fx.fadeAndStopAudio(600);
  narrator.cancel(300);
  await new Promise<void>((resolve) => setTimeout(resolve, 600));

  saveTutorialState(
    outcome.kind === 'completed'
      ? { completed: true, skipped: false }
      : { completed: false, skipped: true }
  );

  coord.abort();
  detachLocaleListener();
  pause.stop();
  loop.stop();
  hintBanner.dispose();
  lowFpsAdvisor.dispose();
  input.dispose();
  // Tear down behavior-owned resources BEFORE the renderer/canvas go away.
  // Render-layer dispose stops looped positional audio (treadmill belt sound,
  // door-open one-shots) and unparents those nodes from the scene graph;
  // sim-layer dispose drops any per-tick state. Without this the WebAudio
  // nodes for the treadmill loop survive teardown and bleed into the splash.
  renderer.arena.disposeBehaviors();
  transport.world.arena.disposeBehaviors();
  canvas.remove();
  subtitleOverlay.remove();
  narrator.dispose();
  fx.dispose();
  renderer.disposeLifecycle(); try { renderer.renderer.dispose?.(); } catch { /* noop */ }

  // Restore the user's bloom/flare prefs before any subsequent match builds.
  CONFIG.render.bloom = savedBloom;
  CONFIG.render.anamorphic = savedAnamorphic;

  fadeEl.remove();
}

function mountFadeToBlackOverlay(durationMs: number): HTMLElement {
  const el = document.createElement('div');
  el.style.cssText = [
    'position: fixed', 'inset: 0', 'z-index: 999',
    'background: black', 'opacity: 0',
    `transition: opacity ${durationMs}ms ease-in-out`,
    'pointer-events: none'
  ].join('; ');
  document.body.appendChild(el);
  // Force a reflow so the next opacity assignment animates rather than
  // skipping straight to the final value.
  void el.offsetWidth;
  el.style.opacity = '1';
  return el;
}

/** One full match cycle: map-select → game → Back-to-Lobby wipe.
 *
 *  Construction model: all match infrastructure (preload, canvas,
 *  transport, renderer, fx, hud, nameTags, GameLoop) is built INSIDE the
 *  map-select preload callback. The GameLoop is started from inside that
 *  callback too — so by the time the map-select bar is full and the
 *  wipe begins, the renderer is already painting frames. The wipe
 *  reveals an already-active scene, not a black gap.
 *
 *  Sim is held frozen via `transport.started=false` until AFTER the wipe
 *  completes — physics still steps (so a stray ragdoll settles cleanly),
 *  but no AI / human inputs are fed in. Once the wipe is done the
 *  caller flips `transport.start()` to release the freeze. The user
 *  never sees AI mid-action because they only see the spawn pose
 *  during the wipe + transport.start() landing on the next frame. */
async function runMatch(config: JoinConfigSummary): Promise<void> {
  const mapScreen = new MapSelectScreen();
  mapScreen.mount();

  // Game-layer handles populated inside the preload, used after the wipe.
  let canvas!: HTMLCanvasElement;
  let input!: Input;
  let transport!: LocalTransport;
  let renderer!: Renderer;
  let roster!: TeamMember[];
  let fx!: FxBus;
  let hud!: Hud;
  let spectatePanel!: SpectatePanel;
  let lowFpsAdvisor!: LowFpsAdvisor;
  let nameTags!: NameTags;
  let loop!: GameLoop;
  let pause!: PauseController;
  let pauseHint!: PauseHintBanner;

  interface ModalHandle { dismiss(): void }
  const modals: { round: ModalHandle | null; game: ModalHandle | null } = { round: null, game: null };
  let resolveEnd: (() => void) | null = null;
  const endPromise = new Promise<void>((res) => { resolveEnd = res; });
  // Hard sim freeze. While true, the GameLoop's onStep is a no-op:
  // transport.tick() doesn't run, world.step() doesn't run, NO physics
  // advance and NO AI input flow. The renderer keeps painting the seed
  // frame so the wipe reveals an animated-looking scene that's actually
  // sitting at the spawn pose. Flipped false AFTER the wipe so "go
  // time" lines up exactly with the screen reveal — the user never sees
  // characters mid-action before the swipe completes.
  let simPaused = true;

  const pickedMap = await mapScreen.show(async (mapId) => {
    if (mapId === 'spaceshipInRings') await preloadSpaceshipAssets();
    if (mapId === 'sinkingShip') await preloadWoodenBoatAssets();
    // Bundled map preload — no-op when there's no bundle under this mapId
    // (until each map is ported). Parallel-safe with the spaceship preload
    // above: they target different async resources.
    await preloadBundle(mapId);

    const app = document.getElementById('app')!;
    canvas = document.createElement('canvas');
    app.appendChild(canvas);

    input = new Input(canvas);
    roster = buildLocalRoster(
      {
        name: config.name,
        color: config.color,
        hatId: config.hatId === 'none' ? null : config.hatId
      },
      PRESET_COLORS,
      randomName
    );
    transport = new LocalTransport(input, { roster, mapId, aiDifficulty: 'easy' });
    renderer = new Renderer(canvas, mapId, transport.world.arena);
    await renderer.init();
    applyUserIblAfterInit(renderer);
    await renderer.awaitAssetsReady();
    renderer.setSimWorld(transport.world);

    for (const m of roster) {
      const palette = paletteFromBase(m.color);
      renderer.addCharacter(m.id, new CharacterView(palette, { hatId: m.hatId }));
    }

    // Seed one frame so the canvas paints something before the GameLoop
    // takes over (and as a fallback in case rAF doesn't fire instantly).
    transport.tick(1 / 60);
    const prev = transport.prevSnapshot();
    const curr = transport.latestSnapshot();
    renderer.render(prev, curr, 1, 0, 1, input.cameraYaw(), input.cameraPitch(), transport.match.focusId);

    // Build fx/hud/nameTags + GameLoop and start the loop. Sim is frozen
    // via transport.started=false (set by transport.start() AFTER the
    // wipe), so transport.tick() steps physics but no inputs feed in —
    // the renderer paints the spawn pose continuously through the wipe.
    fx = new FxBus(renderer, transport.match.localPlayer().id);
    hud = new Hud({ match: transport.match });
    spectatePanel = new SpectatePanel();
    lowFpsAdvisor = new LowFpsAdvisor();
    nameTags = new NameTags(renderer, transport.match);
    const debug = new DebugPanel(transport, input, renderer);
    void debug;

    // Pause / settings during the local match. Esc, gamepad Start, and
    // the mobile ⋯ button all open the same modal. Suppressed while a
    // round-end / game-end modal is up. The orange "Leave Game" action
    // resolves the match's endPromise, dropping straight into the
    // standard back-to-lobby teardown path.
    pause = new PauseController({
      input,
      isTutorial: false,
      isBlocked: () => modals.round !== null || modals.game !== null,
      onLeaveMatch: () => resolveEnd?.()
    });
    pause.start();
    pauseHint = new PauseHintBanner(input);

    const BASE_SATURATION = CONFIG.render.saturation;
    const REPLAY_SATURATION = 0.55;
    const REPLAY_BLUR_MAX_PX = 12;
    let lastAppliedBlurPx = 0;
    let prevPhase: MatchPhase | '' = '';

    loop = new GameLoop(
      (dt) => {
        // Hard freeze during the map-select wipe. Skip transport.tick()
        // entirely so world.step() doesn't run, no physics advance, no
        // AI inputs flow. The renderer keeps painting the static seed
        // frame so the wipe reveals an unmoving scene exactly as the
        // user expects.
        if (simPaused) return;
        transport.tick(dt);

        // Spectate-cycle edges — only while the local player is DEAD and
        // the round is still being played out.
        const localId = transport.match.localPlayer().id;
        const localCh = transport.latestSnapshot()?.characters.find((c) => c.id === localId);
        const localDead = !!localCh && (localCh.wasted || localCh.ringedOut);
        input.setSpectateVisible(localDead && transport.match.phase === 'playing');
        input.setLocalMidAir(!!localCh && localCh.bothAir);
        const l = input.consumeSpectateLeft();
        const r = input.consumeSpectateRight();
        spectatePanel.syncFrom(input, transport.match, localDead);
        if (transport.match.phase === 'playing' && localDead) {
          if (l > 0) spectatePanel.flash('left');
          if (r > 0) spectatePanel.flash('right');
          for (let i = 0; i < l; i++) transport.cycleFocus(-1);
          for (let i = 0; i < r; i++) transport.cycleFocus(+1);
        }

        // Phase-change edge.
        const phase = transport.match.phase;
        if (phase !== prevPhase) {
          if (phase === 'playing' && prevPhase !== 'playing') {
            showFightBanner();
          } else if (phase === 'roundOver') {
            modals.round = showRoundEndModal(transport.match, {
              onContinue: () => {
                modals.round = null;
                transport.advanceToNextRound();
              }
            });
          } else if (phase === 'gameOver') {
            playGameEndSfx(transport.match.lastRoundWinner === transport.match.localTeam());
            modals.game = showGameEndModal(transport.match, {
              onBackToLobby: () => {
                modals.game = null;
                resolveEnd?.();
              }
            });
          }
          prevPhase = phase;
        }
      },
      (alpha, realDt) => {
        const liveSnap = transport.latestSnapshot();
        if (liveSnap) fx.koReplay.armIfNeeded(liveSnap);
        fx.koReplay.step(realDt, liveSnap ? liveSnap.tick : 0);

        let renderPrev = transport.prevSnapshot();
        let renderCurr = liveSnap;
        let renderAlpha = alpha;
        if (fx.koReplay.active() && liveSnap) {
          const headF = fx.koReplay.headTick();
          const ti = Math.floor(headF);
          renderCurr = transport.snapshotAtTick(ti) ?? liveSnap;
          renderPrev = transport.snapshotAtTick(ti - 1) ?? renderPrev;
          renderAlpha = headF - ti;
        }

        if (renderCurr) {
          fx.step(realDt, renderCurr);
          hud.update(renderCurr);
          nameTags.update(renderCurr);
        }

        const desat = fx.koReplay.desatAmount();
        renderer.post.setSaturation(BASE_SATURATION + (REPLAY_SATURATION - BASE_SATURATION) * desat);

        const blurPx = fx.koReplay.blurAmount() * REPLAY_BLUR_MAX_PX;
        const quantized = Math.round(blurPx * 10) / 10;
        if (quantized !== lastAppliedBlurPx) {
          canvas.style.filter = quantized > 0 ? `blur(${quantized}px)` : '';
          lastAppliedBlurPx = quantized;
        }

        hud.tickFps(realDt);
        pauseHint.tick();
        renderer.render(
          renderPrev, renderCurr, renderAlpha, realDt,
          fx.timeScale(), input.cameraYaw(), input.cameraPitch(),
          transport.match.focusId
        );
      }
    );
    loop.start();
  }, (pickedMapId) => {
    // Fires 100ms before the map-select wipe begins. Game-layer objects are
    // already constructed and the GameLoop is already painting; this is
    // the right moment to fade the home music so the audio crossfade
    // lands on the same frame the visual wipe begins.
    void pickedMapId;
    void menuAudio.fadeMusic(0, 700);
  });

  // User cancelled before picking a map (controller B / Esc on map select).
  // No game-layer infrastructure was constructed — the preload callback
  // above only fires on commit — so we can return immediately and let the
  // outer loop re-show the home screen.
  if (pickedMap === null) return;

  // Map-select has wiped out and the game canvas is fully exposed. The
  // GameLoop has been painting the spawn pose throughout the wipe;
  // release the sim freeze now so the world ticks and AI inputs flow.
  // Order matters — flip simPaused FIRST so the next loop tick sees
  // an unfrozen sim, THEN transport.start() so AI inputs are ready.
  simPaused = false;
  transport.start();
  // Map-select wipe is fully off-screen — kick off the map's background
  // music (FxBus deferred playback at construction so the music doesn't
  // bleed under the still-visible map-select panel).
  fx.startMusic();

  (window as any).debug = {
    transport, renderer, fx, config,
    reset: () => transport.forceReset()
  };

  await endPromise;

  // --- Teardown on Back-to-Lobby ---
  // Pre-mount the home screen BEHIND the live game so the swipe reveals it
  // as the wipe clears (rather than cutting to black until it mounts). We
  // hold the HomeScreen reference here and hand it off to the match loop
  // after teardown so showHomeStandalone doesn't construct a second one.
  const home = prepareReturnHome();

  const swipeMs = 700;
  // Instead of a black overlay that wipes OFF to reveal the still-live
  // game, we now wipe the game-layer elements themselves (the canvas, the
  // HUD, the still-visible game-end modal). As they clip out, the already-
  // mounted home — sitting at a lowered z-index for this one moment — is
  // what's revealed behind them.
  const gameWipeEls = [
    document.getElementById('app'),
    document.getElementById('hud'),
    document.getElementById('match-modals')
  ].filter((el): el is HTMLElement => el !== null);
  // Home was mounted with z-index 5 so it sits below the game layer. #app
  // in particular has z-index:auto by default and would otherwise paint
  // BELOW home (later-mounted positioned elements paint above) — so we
  // temporarily boost the game layer's z-indices to guarantee it stacks
  // on top of home during the wipe. Restored (empty string → stylesheet
  // defaults reapply) after the wipe.
  const originalGameZ = gameWipeEls.map((el) => el.style.zIndex);
  for (const el of gameWipeEls) {
    const currentZ = parseInt(el.style.zIndex || '', 10);
    // If nothing's set inline, use a safe high default. Otherwise keep
    // whatever was already there as long as it's >= 50.
    if (!Number.isFinite(currentZ) || currentZ < 50) el.style.zIndex = '50';
  }
  const audioFade = Promise.all([
    fx.fadeAndStopAudio(swipeMs),
    fadeOutDomAudio(swipeMs)
  ]);
  // Start the home music crossfade at the same moment the wipe begins so
  // the ear hears the swap as one transition, not two.
  menuAudio.fadeMusic(1.0, swipeMs);
  await wipeGameLayerOut(gameWipeEls, swipeMs);
  await audioFade;
  modals.round?.dismiss();
  modals.game?.dismiss();
  loop.stop();
  pause.stop();
  pauseHint.dispose();
  fx.dispose();
  hud.dispose();
  lowFpsAdvisor.dispose();
  nameTags.dispose();
  input.dispose();
  canvas.remove();
  document.getElementById('match-modals')?.remove();
  renderer.disposeLifecycle(); try { renderer.renderer.dispose?.(); } catch { /* no-op on older three */ }
  delete (window as any).debug;
  // Restore the game-layer wrappers to their normal state for the next
  // match (they persist across match cycles — #app is in index.html).
  for (let i = 0; i < gameWipeEls.length; i++) {
    const el = gameWipeEls[i]!;
    el.style.clipPath = '';
    el.style.transition = '';
    el.style.zIndex = originalGameZ[i] ?? '';
  }
  // Promote the home back to its normal z-index now that nothing's in front
  // of it, and activate its nav so the returning user can drive the menu.
  home.root.style.zIndex = '';
  home.activateNav();
  // Hand the mounted home off to the outer loop.
  (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = home;
}

/** Online match flow.
 *
 *  Stage 1 (party panel + matchmaking): home stays mounted and the
 *    OnlineFlowController drives the party UI on top of it.
 *  Stage 2 (match): seat reservation consumed, home disposed, regular
 *    match flow takes over.
 *
 *  Returns when either: (a) user confirms "Leave online play" → home
 *  is preserved and stashed for the outer loop, or (b) a match completes
 *  → fresh home is created via prepareReturnHome and stashed. */
async function runOnlineMatch(
  config: JoinConfigSummary,
  homeScreen: HomeScreen,
  autoJoin?: JoinIntent
): Promise<void> {
  // Region resolution. autoJoin (link-driven) wins over user preference;
  // otherwise the user's stored preference, otherwise the first env-listed
  // region. The client + party room are torn down + rebuilt mid-session
  // when the owner switches regions, so both `colyseus` and `currentRegion`
  // are mutable here.
  const preferredRegion =
    getRegionById(loadPreferredRegion()) ?? getDefaultRegion();
  let currentRegion: Region = autoJoin
    ? (getRegionById(autoJoin.regionId) ?? preferredRegion)
    : preferredRegion;
  // True until the user has either successfully joined the linked party or
  // we fell back to a fresh solo party in their preferred region. While
  // true the region picker stays locked.
  let isGuestInThisRegion = false;
  let colyseus = new ColyseusClient(currentRegion.url);

  const memberOptions = (): Record<string, unknown> => {
    const a = accountState.get();
    const token = (() => {
      try { return localStorage.getItem('games:softAuthToken') ?? undefined; } catch { return undefined; }
    })();
    return {
      name: config.name,
      color: config.color,
      hatId: config.hatId === 'none' ? '' : config.hatId,
      level: a?.level ?? 1,
      ...(token ? { accountToken: token } : {}),
    };
  };

  const identityForController = (): { name: string; color: string; hatId: string; level: number } => {
    const a = accountState.get();
    return {
      name: config.name,
      color: config.color,
      hatId: config.hatId === 'none' ? '' : (config.hatId as string),
      level: a?.level ?? 1,
    };
  };

  // Initial party room. autoJoin path: connect to the linked region and
  // attempt to join the target party via the existing party:joinByCode
  // mechanism. On success we land in the target party with a temporary
  // "guest" flag so the region picker stays locked. On failure (lobby
  // gone, full, etc.) we drop the guest flag, switch back to the user's
  // preferred region, and start a fresh solo party there with a "lobby
  // unavailable" modal.
  let partyRoom: Room<unknown> | null = null;
  if (autoJoin) {
    const outcome = await tryAutoJoin(colyseus, config, autoJoin);
    if (outcome.kind === 'ok') {
      partyRoom = outcome.room;
      isGuestInThisRegion = true;
    } else {
      // Roll back to preferred region. Build a fresh client if we have to
      // change region (the autoJoin region might equal preferred, in
      // which case we keep the existing client).
      if (currentRegion.id !== preferredRegion.id) {
        try { /* we never got into a real party; nothing to leave */ } catch { /* noop */ }
        currentRegion = preferredRegion;
        colyseus = new ColyseusClient(currentRegion.url);
      }
      try {
        partyRoom = await colyseus.create('party', memberOptions());
      } catch (err) {
        console.error('[runOnlineMatch] fallback solo create after failed autoJoin', err);
        showConnectError((err as Error).message);
        homeScreen.exitOnlineMode();
        (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = homeScreen;
        return;
      }
      // Modal stays up via its own dismiss on user action.
      await new Promise<void>((resolve) => {
        showLobbyUnavailableModal(() => resolve());
      });
    }
  } else {
    try {
      partyRoom = await colyseus.create('party', memberOptions());
    } catch (err) {
      console.error('[runOnlineMatch] failed to create party', err);
      showConnectError((err as Error).message);
      homeScreen.exitOnlineMode();
      (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = homeScreen;
      return;
    }
  }

  // Loop on join-by-code party swaps + region migrations — each iteration
  // represents being connected to a particular PartyRoom on a particular
  // server. A successful joinByCode emits a reservation message; we
  // leave the current room, consume it, and re-enter the loop with the
  // new partyRoom. A region switch tears down the colyseus client too
  // and rebuilds against the target region.
  let go: GoPayload | null = null;
  let leaveOnline = false;

  partyLoop:
  while (!leaveOnline && !go) {
    const room = partyRoom!;
    type FlowOutcome =
      | { kind: 'leaveOnline' }
      | { kind: 'go'; payload: GoPayload }
      | { kind: 'joinByCodeOk'; reservation: unknown }
      | { kind: 'regionSwitched'; regionId: string; newClient: ColyseusClient; newRoom: Room<unknown>; modal: { dismiss(): void } }
      | { kind: 'memberMigrate'; regionId: string; roomId: string; modal: { dismiss(): void } }
      | { kind: 'phaseVote' };

    const outcome = await new Promise<FlowOutcome>((resolve) => {
      const controller = new OnlineFlowController(
        room as never,
        homeScreen,
        {
          onEnterVote: () => resolve({ kind: 'phaseVote' }),
          onLaunching: () => { /* go message comes through onMessage below */ },
          onMatchAborted: (reason) => {
            showConnectError(t('errors.matchAborted', { reason }));
          },
          onLeaveOnline: () => resolve({ kind: 'leaveOnline' }),
          onRegionSwitched: ({ regionId, newClient, newRoom, modal }) => {
            resolve({
              kind: 'regionSwitched',
              regionId,
              newClient,
              newRoom: newRoom as Room<unknown>,
              modal
            });
          },
          onMemberMigrate: ({ regionId, roomId, modal }) => {
            resolve({ kind: 'memberMigrate', regionId, roomId, modal });
          }
        },
        currentRegion.id,
        identityForController()
      );
      controller.start();

      // The party room emits 'go' (passed through from PartyRoom.applyLaunch).
      // We resolve directly; the controller's onLaunching is informational.
      room.onMessage('go', (msg: GoPayload) => {
        resolve({ kind: 'go', payload: msg });
      });
      room.onMessage('party:joinByCode:ok', (msg: { reservation: unknown }) => {
        resolve({ kind: 'joinByCodeOk', reservation: msg.reservation });
      });

      // Tear-down happens in the post-resolve switch below. Stash the
      // controller on the room so we can reach it.
      (room as unknown as { _controller?: OnlineFlowController })._controller = controller;
    });

    const ctrl = (room as unknown as { _controller?: OnlineFlowController })._controller;

    if (outcome.kind === 'leaveOnline') {
      await ctrl?.dispose();
      try { await room.leave(); } catch { /* noop */ }
      leaveOnline = true;
      break partyLoop;
    }
    if (outcome.kind === 'joinByCodeOk') {
      await ctrl?.dispose();
      try { await room.leave(); } catch { /* noop */ }
      try {
        partyRoom = await colyseus.consumeSeatReservation(outcome.reservation as never);
        // Loop continues with the new room.
        continue partyLoop;
      } catch (err) {
        console.error('[runOnlineMatch] consumeSeatReservation (party swap) failed', err);
        showConnectError(t('errors.couldNotJoinParty', { message: (err as Error).message }));
        leaveOnline = true;
        break partyLoop;
      }
    }
    if (outcome.kind === 'regionSwitched') {
      // Owner finished bootstrapping on the new region. Tear down the
      // local view of the OLD room + client; the OLD PartyRoom server-
      // side re-broadcast 'party:migrate' to the other members already
      // and disconnects itself shortly.
      await ctrl?.dispose();
      try { await room.leave(); } catch { /* noop */ }
      colyseus = outcome.newClient;
      currentRegion = getRegionById(outcome.regionId) ?? currentRegion;
      partyRoom = outcome.newRoom;
      // Owner's own pick — they're the OWNER of this new room (they
      // created it), not a guest.
      isGuestInThisRegion = false;
      outcome.modal.dismiss();
      continue partyLoop;
    }
    if (outcome.kind === 'memberMigrate') {
      // Non-owner: follow the owner to the new region. Build a fresh
      // client there and joinById the room the owner just created.
      await ctrl?.dispose();
      try { await room.leave(); } catch { /* noop */ }
      const target = getRegionById(outcome.regionId);
      if (!target) {
        outcome.modal.dismiss();
        showConnectError(`unknown region: ${outcome.regionId}`);
        leaveOnline = true;
        break partyLoop;
      }
      const newClient = new ColyseusClient(target.url);
      try {
        partyRoom = await newClient.joinById<unknown>(outcome.roomId, memberOptions());
        colyseus = newClient;
        currentRegion = target;
        // We didn't pick this region — we were dragged here by the
        // owner. Picker stays locked; preferred region in localStorage
        // is untouched.
        isGuestInThisRegion = true;
        outcome.modal.dismiss();
        continue partyLoop;
      } catch (err) {
        console.error('[runOnlineMatch] member migrate joinById failed', err);
        outcome.modal.dismiss();
        // Fall back to a fresh solo party in the user's preferred
        // region with a "lobby unavailable" modal.
        currentRegion = preferredRegion;
        colyseus = new ColyseusClient(currentRegion.url);
        try {
          partyRoom = await colyseus.create('party', memberOptions());
          isGuestInThisRegion = false;
        } catch (createErr) {
          console.error('[runOnlineMatch] post-migrate fallback create failed', createErr);
          showConnectError((createErr as Error).message);
          leaveOnline = true;
          break partyLoop;
        }
        await new Promise<void>((resolve) => {
          showLobbyUnavailableModal(() => resolve());
        });
        continue partyLoop;
      }
    }
    if (outcome.kind === 'phaseVote') {
      // Mount MapVoteScreen against the same partyRoom. The vote phase is
      // owned by the matchmaker which broadcasts vote totals + countdown
      // through PartyState fields the screen already reads. The screen
      // owns its own morph + preload + wipe transition once 'go' arrives.
      const voteScreen = new MapVoteScreen();
      let preparedHandles: MatchHandles | null = null;
      let voteGo: GoPayload | null = null;
      try {
        voteGo = await voteScreen.show(
          room as never,
          async (goPayload: GoPayload) => {
            try {
              preparedHandles = await prepareMatchInfrastructure(colyseus, goPayload, homeScreen);
            } catch (err) {
              console.error('[runOnlineMatch] prepare during morph failed', err);
            }
          },
          () => { void menuAudio.fadeMusic(0, 700); },
          () => { ctrl?.lockForLoading(); }
        );
      } catch (err) {
        console.error('[runOnlineMatch] vote phase failed', err);
      }
      await ctrl?.dispose();
      if (!voteGo) {
        // Vote closed without a 'go' — server failure or aborted.
        showConnectError(t('errors.couldNotStart'));
        try { await room.leave(); } catch { /* noop */ }
        leaveOnline = true;
        break partyLoop;
      }
      // Now that the wipe has completed (the canvas is fully exposed),
      // hand off to the post-prep runner which sends match:ready and
      // waits for the match to play through.
      await runOnlineMatchFromGo(colyseus, voteGo, config, homeScreen, 0, preparedHandles ?? undefined);
      return;
    }
    if (outcome.kind === 'go') {
      await ctrl?.dispose();
      go = outcome.payload;
      break partyLoop;
    }
  }

  // Persist preferred region only if the user is the OWNER of their
  // current party — i.e. they explicitly chose this region or were the
  // one who created it. If they're a guest, preserve their existing
  // preference untouched.
  if (!isGuestInThisRegion) savePreferredRegion(currentRegion.id);

  // Stage transition.
  if (leaveOnline) {
    homeScreen.exitOnlineMode();
    (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = homeScreen;
    return;
  }
  if (!go) {
    homeScreen.exitOnlineMode();
    (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = homeScreen;
    return;
  }

  await runOnlineMatchFromGo(colyseus, go, config, homeScreen);
}

/** Internal: an auto-join via `?join=` URL link. Connects to the linked
 *  region, creates a temporary solo party, sends 'party:joinByCode' for
 *  the linked code, awaits the reservation, leaves the temp party,
 *  consumes the reservation. Returns the live target party room on
 *  success, or { kind: 'fail' } so the caller can fall back to a fresh
 *  solo party + LobbyUnavailable modal. */
async function tryAutoJoin(
  colyseus: ColyseusClient,
  config: JoinConfigSummary,
  autoJoin: JoinIntent
): Promise<{ kind: 'ok'; room: Room<unknown> } | { kind: 'fail'; reason: string }> {
  const a = accountState.get();
  const token = (() => {
    try { return localStorage.getItem('games:softAuthToken') ?? undefined; } catch { return undefined; }
  })();
  const memberOpts: Record<string, unknown> = {
    name: config.name,
    color: config.color,
    hatId: config.hatId === 'none' ? '' : config.hatId,
    level: a?.level ?? 1,
    ...(token ? { accountToken: token } : {}),
  };
  let temp: Room<unknown> | null = null;
  try {
    temp = await colyseus.create<unknown>('party', memberOpts);
  } catch (err) {
    return { kind: 'fail', reason: (err as Error).message || 'create-failed' };
  }
  const tempRoom = temp;
  // Race the reservation reply against a generous timeout in case the
  // server doesn't reply at all.
  const settled = await new Promise<{ ok: true; reservation: unknown } | { ok: false; reason: string }>((resolve) => {
    const timer = setTimeout(() => resolve({ ok: false, reason: 'timeout' }), 6_000);
    tempRoom.onMessage('party:joinByCode:ok', (msg: { reservation: unknown }) => {
      clearTimeout(timer);
      resolve({ ok: true, reservation: msg.reservation });
    });
    tempRoom.onMessage('party:joinByCode:failed', (msg: { reason?: string }) => {
      clearTimeout(timer);
      resolve({ ok: false, reason: msg.reason ?? 'unknown' });
    });
    try { tempRoom.send('party:joinByCode', { code: autoJoin.joinCode }); }
    catch (err) { clearTimeout(timer); resolve({ ok: false, reason: (err as Error).message || 'send-failed' }); }
  });
  // Leave the temp room regardless of outcome.
  try { await tempRoom.leave(); } catch { /* noop */ }
  if (!settled.ok) return { kind: 'fail', reason: settled.reason };
  try {
    const target = await colyseus.consumeSeatReservation<unknown>(settled.reservation as never);
    return { kind: 'ok', room: target };
  } catch (err) {
    return { kind: 'fail', reason: (err as Error).message || 'reservation-failed' };
  }
}

/** Match-side of the online flow: from the moment the matchmaker emits
 *  'go' through to teardown / play-again regroup.
 *
 *  Two callers feed this:
 *  1. The vote-screen morph in runOnlineMatch — passes
 *     `alreadyPrepared` because the preload callback ran
 *     prepareMatchInfrastructure during the bar drain. By the time we
 *     get here the wipe is finished and the match is already rendering
 *     behind it; we just need to send match:ready and run to completion.
 *  2. Fallback paths (e.g. 'go' arriving without phase=vote, or
 *     play-again regroup without a vote screen) — pass undefined and
 *     this helper shows a connecting modal while it constructs.
 *
 *  `homeToDisposeAtMatchStart` is the HomeScreen that was hosting the
 *  party UI — disposed inside prepareMatchInfrastructure (or, for the
 *  alreadyPrepared path, was disposed during the morph). */
async function runOnlineMatchFromGo(
  colyseus: ColyseusClient,
  go: GoPayload,
  config: JoinConfigSummary,
  homeToDisposeAtMatchStart: HomeScreen,
  playAgainDepth = 0,
  alreadyPrepared?: MatchHandles
): Promise<void> {
  let handles: MatchHandles;
  if (alreadyPrepared) {
    handles = alreadyPrepared;
  } else {
    const connectingModal = showConnectingModal(t('connecting.joining'));
    try {
      handles = await prepareMatchInfrastructure(colyseus, go, homeToDisposeAtMatchStart);
    } catch (err) {
      console.error('[runOnlineMatch] prepareMatchInfrastructure failed', err);
      connectingModal.dismiss();
      showConnectError(t('errors.couldNotJoinMatch', { message: (err as Error).message }));
      return;
    }
    connectingModal.dismiss();
  }
  await runMatchToCompletion(handles, config, playAgainDepth);
}


/** After a play-again regroup the new PartyRoom is auto-searching with
 *  NPC fill — this helper mounts an OnlineFlowController against it and
 *  waits for the next match boundary, exactly like runOnlineMatch's
 *  party-loop inner section. Recursion-light: we don't loop on
 *  joinByCode here (the regroup party is already final). */
async function continueOnlineMatchAfterRegroup(
  colyseus: ColyseusClient,
  partyRoom: Parameters<typeof showConnectingModal>[0] extends never ? never : Awaited<ReturnType<typeof colyseus.consumeSeatReservation>>,
  homeScreen: HomeScreen,
  config: JoinConfigSummary,
  playAgainDepth = 0
): Promise<void> {
  type FlowOutcome =
    | { kind: 'leaveOnline' }
    | { kind: 'go'; payload: GoPayload }
    | { kind: 'phaseVote' };
  const outcome = await new Promise<FlowOutcome>((resolve) => {
    // Regroup parties don't expose region switching (the play-again
    // path is post-match-with-NPC-fill — auto-search fires immediately
    // on join, so the picker would never have time to be useful). Pass
    // the user's preferred region as the "current" id and lock the
    // picker; controller still wires onRegionSwitched / onMemberMigrate
    // handlers but they'd never fire while the picker is locked.
    const regionId = loadPreferredRegion() ?? getDefaultRegion().id;
    const controller = new OnlineFlowController(
      partyRoom as never,
      homeScreen,
      {
        onEnterVote: () => resolve({ kind: 'phaseVote' }),
        onLaunching: () => { /* go message handled below */ },
        onMatchAborted: (reason) => { showConnectError(t('errors.matchAborted', { reason })); },
        onLeaveOnline: () => resolve({ kind: 'leaveOnline' }),
        onRegionSwitched: ({ modal }) => { modal.dismiss(); /* not reachable in regroup */ },
        onMemberMigrate: ({ modal }) => { modal.dismiss(); /* not reachable in regroup */ }
      },
      regionId,
      {
        name: config.name,
        color: config.color,
        hatId: config.hatId === 'none' ? '' : (config.hatId as string),
        level: 7
      }
    );
    controller.start();
    partyRoom.onMessage('go', (msg: GoPayload) => resolve({ kind: 'go', payload: msg }));
    (partyRoom as unknown as { _controller?: OnlineFlowController })._controller = controller;
  });
  const ctrl = (partyRoom as unknown as { _controller?: OnlineFlowController })._controller;
  if (outcome.kind === 'leaveOnline') {
    await ctrl?.dispose();
    try { await partyRoom.leave(); } catch { /* noop */ }
    homeScreen.exitOnlineMode();
    (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = homeScreen;
    return;
  }
  let go: GoPayload | null = null;
  let preparedHandles: MatchHandles | null = null;
  if (outcome.kind === 'phaseVote') {
    const voteScreen = new MapVoteScreen();
    try {
      go = await voteScreen.show(
        partyRoom as never,
        async (goPayload: GoPayload) => {
          try {
            preparedHandles = await prepareMatchInfrastructure(colyseus, goPayload, homeScreen);
          } catch (err) {
            console.error('[regroup] prepare during morph failed', err);
          }
        },
        () => { void menuAudio.fadeMusic(0, 700); },
        () => { ctrl?.lockForLoading(); }
      );
    } catch (err) { console.error('[regroup] vote phase failed', err); }
    await ctrl?.dispose();
  } else {
    await ctrl?.dispose();
    go = outcome.payload;
  }
  if (!go) {
    homeScreen.exitOnlineMode();
    (runMatch as unknown as { _pendingReturnHome?: HomeScreen })._pendingReturnHome = homeScreen;
    return;
  }
  // Hand off to the shared post-go path. With preparedHandles, the
  // post-go runner skips the connecting overlay (everything is already
  // built; we just send match:ready and run to completion).
  await runOnlineMatchFromGo(colyseus, go, config, homeScreen, playAgainDepth, preparedHandles ?? undefined);
}

/** Register no-op handlers on the freshly-joined GameRoom for every message
 *  type the server emits at high frequency (bones binary `1`, plus the
 *  per-tick FX events). Without this, the @colyseus/sdk emits one warning per
 *  message until ColyseusTransport's constructor gets around to registering
 *  its real handlers — which can flood the console while the bundle preloads
 *  + WebGPU initialises. The transport adds its OWN handlers later; the SDK
 *  fans out to every handler registered for a given type, so these stubs stay
 *  inert and don't double-process anything.
 *
 *  Special case for `'ping'`: the server's 2s ping cycle starts immediately
 *  on room creation, and the FIRST ping often lands before the transport's
 *  real handler is installed. We do the pong echo right here so no ping is
 *  lost — the transport's later handler also calls `room.send('pong', ...)`
 *  but the server's pong handler is idempotent on the latest RTT measurement
 *  so a duplicate echo is harmless. */
function installEarlyMessageSinks(room: {
  onMessage: (type: string | number, cb: (msg: unknown) => void) => void;
  send: (type: string, payload: unknown) => void;
}): void {
  const noop = (): void => { /* swallowed during the connect window */ };
  room.onMessage(1, noop);
  room.onMessage('impact', noop);
  room.onMessage('footstep', noop);
  room.onMessage('swing', noop);
  room.onMessage('ping', (msg: unknown) => {
    const t = (msg as { t?: number } | null)?.t;
    if (typeof t !== 'number') return;
    try { room.send('pong', { t }); } catch { /* noop */ }
  });
}

/** Resolves once `predicate(state)` is true OR after `timeoutMs`. Used to
 *  block the online-match boot until the server's first state patch has
 *  populated the roster. */
function waitForState<S>(room: { state: S; onStateChange: (cb: (s: S) => void) => void }, predicate: (s: S) => boolean, timeoutMs: number): Promise<void> {
  return new Promise<void>((resolve) => {
    if (predicate(room.state)) { resolve(); return; }
    const timer = setTimeout(() => { resolve(); }, timeoutMs);
    room.onStateChange((s: S) => {
      if (predicate(s)) {
        clearTimeout(timer);
        resolve();
      }
    });
  });
}

function showConnectError(message: string): void {
  // Lightweight error toast for the rare cold-path failure. Not styled to the
  // hilt — these are last-resort messages and mostly indicate a misconfigured
  // server URL or a network hiccup before the queue room loads.
  const el = document.createElement('div');
  el.style.cssText = `
    position: fixed; left: 50%; top: 40px; transform: translateX(-50%);
    background: rgba(170, 30, 30, 0.92); color: #fff; padding: 14px 22px;
    border-radius: 14px; z-index: 1000; font-family: 'Chewy', sans-serif;
    font-size: 22px; box-shadow: 0 8px 16px rgba(0,0,0,0.4);
  `;
  el.textContent = message;
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 5000);
}

/** Mount the MapEditorScreen and await its exit. Playtest button inside the
 *  editor chains through runEditorPlaytest with a short-lived roster + the
 *  editor's in-memory map json. */
async function runEditor(): Promise<void> {
  // Kill whatever mock home config was being used; the editor owns the
  // screen from here.
  const editor = new MapEditorScreen({
    onPlaytest: async (json) => {
      await runEditorPlaytest(json);
    }
  });
  await editor.show();
}

/** Run a playtest match off an in-memory MapJSON snapshot from the editor.
 *  Reuses the real match path — same physics, same renderer, same HUD —
 *  except:
 *    - The map comes from a transient bundle rather than the registry.
 *    - A floating Stop button (Esc also works) resolves the end promise.
 *    - Teardown doesn't do the back-to-lobby wipe; control returns straight
 *      to the editor. */
async function runEditorPlaytest(editorJson: MapJSON): Promise<void> {
  const baseBundleId = editorJson.id;
  // The base bundle must exist and have its behaviors loaded so the transient
  // bundle can reuse them. A fresh editor session will have already called
  // preloadBundle via EditorViewport.loadBundle.
  await preloadBundle(baseBundleId);
  if (baseBundleId === 'spaceshipInRings') await preloadSpaceshipAssets();
  if (baseBundleId === 'sinkingShip') await preloadWoodenBoatAssets();
  registerTransientBundle(editorJson, baseBundleId);

  const app = document.getElementById('app')!;
  const canvas = document.createElement('canvas');
  app.appendChild(canvas);

  const input = new Input(canvas);
  // Hardcoded playtest roster: local player + 3 AI bots on a 2v2.
  const roster = buildLocalRoster(
    { name: 'Playtester', color: '#ffb84d', hatId: null },
    PRESET_COLORS,
    randomName
  );
  const transport = new LocalTransport(input, {
    roster, mapId: TRANSIENT_BUNDLE_ID, aiDifficulty: 'easy'
  });
  const renderer = new Renderer(canvas, TRANSIENT_BUNDLE_ID, transport.world.arena);
  await renderer.init();
  applyUserIblAfterInit(renderer);
  await renderer.awaitAssetsReady();
  renderer.setSimWorld(transport.world);
  for (const m of roster) {
    const palette = paletteFromBase(m.color);
    renderer.addCharacter(m.id, new CharacterView(palette, { hatId: m.hatId }));
  }

  const fx = new FxBus(renderer, transport.match.localPlayer().id);
  // Editor playtest has no transition wipe — start music immediately so
  // playtesting behaves like a regular match.
  fx.startMusic();
  const hud = new Hud({ match: transport.match });
  const spectatePanel = new SpectatePanel();
  const lowFpsAdvisor = new LowFpsAdvisor();
  const nameTags = new NameTags(renderer, transport.match);
  const debug = new DebugPanel(transport, input, renderer);
  void debug;

  // Floating Stop button + Esc keybinding.
  const stopBtn = document.createElement('button');
  stopBtn.id = 'playtest-stop';
  stopBtn.textContent = t('playtest.stop');
  document.body.appendChild(stopBtn);

  let resolveEnd: (() => void) | null = null;
  const endPromise = new Promise<void>((res) => { resolveEnd = res; });
  const stop = (): void => { resolveEnd?.(); resolveEnd = null; };
  stopBtn.onclick = stop;
  const onKeyStop = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') stop();
  };
  addEventListener('keydown', onKeyStop);

  const BASE_SATURATION = CONFIG.render.saturation;
  const REPLAY_SATURATION = 0.55;
  const REPLAY_BLUR_MAX_PX = 12;
  let lastAppliedBlurPx = 0;

  interface ModalHandle { dismiss(): void }
  const modals: { round: ModalHandle | null; game: ModalHandle | null } = { round: null, game: null };
  let prevPhase: MatchPhase | '' = '';

  const loop = new GameLoop(
    (dt) => {
      transport.tick(dt);
      const localId = transport.match.localPlayer().id;
      const localCh = transport.latestSnapshot()?.characters.find((c) => c.id === localId);
      const localDead = !!localCh && (localCh.wasted || localCh.ringedOut);
      input.setSpectateVisible(localDead && transport.match.phase === 'playing');
      input.setLocalMidAir(!!localCh && localCh.bothAir);
      const l = input.consumeSpectateLeft();
      const r = input.consumeSpectateRight();
      spectatePanel.syncFrom(input, transport.match, localDead);
      if (transport.match.phase === 'playing' && localDead) {
        if (l > 0) spectatePanel.flash('left');
        if (r > 0) spectatePanel.flash('right');
        for (let i = 0; i < l; i++) transport.cycleFocus(-1);
        for (let i = 0; i < r; i++) transport.cycleFocus(+1);
      }
      const phase = transport.match.phase;
      if (phase !== prevPhase) {
        if (phase === 'playing' && prevPhase !== 'playing') {
          showFightBanner();
        } else if (phase === 'roundOver') {
          modals.round = showRoundEndModal(transport.match, {
            onContinue: () => {
              modals.round = null;
              transport.advanceToNextRound();
            }
          });
        } else if (phase === 'gameOver') {
          playGameEndSfx(transport.match.lastRoundWinner === transport.match.localTeam());
          modals.game = showGameEndModal(transport.match, {
            onBackToLobby: () => {
              modals.game = null;
              stop();
            }
          });
        }
        prevPhase = phase;
      }
    },
    (alpha, realDt) => {
      const liveSnap = transport.latestSnapshot();
      if (liveSnap) fx.koReplay.armIfNeeded(liveSnap);
      fx.koReplay.step(realDt, liveSnap ? liveSnap.tick : 0);

      let renderPrev = transport.prevSnapshot();
      let renderCurr = liveSnap;
      let renderAlpha = alpha;
      if (fx.koReplay.active() && liveSnap) {
        const headF = fx.koReplay.headTick();
        const ti = Math.floor(headF);
        renderCurr = transport.snapshotAtTick(ti) ?? liveSnap;
        renderPrev = transport.snapshotAtTick(ti - 1) ?? renderPrev;
        renderAlpha = headF - ti;
      }
      if (renderCurr) {
        fx.step(realDt, renderCurr);
        hud.update(renderCurr);
        nameTags.update(renderCurr);
      }
      const desat = fx.koReplay.desatAmount();
      renderer.post.setSaturation(BASE_SATURATION + (REPLAY_SATURATION - BASE_SATURATION) * desat);
      const blurPx = fx.koReplay.blurAmount() * REPLAY_BLUR_MAX_PX;
      const quantized = Math.round(blurPx * 10) / 10;
      if (quantized !== lastAppliedBlurPx) {
        canvas.style.filter = quantized > 0 ? `blur(${quantized}px)` : '';
        lastAppliedBlurPx = quantized;
      }
      hud.tickFps(realDt);
      renderer.render(
        renderPrev, renderCurr, renderAlpha, realDt,
        fx.timeScale(), input.cameraYaw(), input.cameraPitch(),
        transport.match.focusId
      );
    }
  );
  loop.start();
  transport.start();

  (window as any).debug = {
    transport, renderer, fx,
    reset: () => transport.forceReset()
  };

  try {
    await endPromise;
  } finally {
    // Teardown. No back-to-lobby wipe — return straight to the editor.
    removeEventListener('keydown', onKeyStop);
    stopBtn.remove();
    modals.round?.dismiss();
    modals.game?.dismiss();
    loop.stop();
    fx.dispose();
    hud.dispose();
    spectatePanel.dispose();
    lowFpsAdvisor.dispose();
    nameTags.dispose();
    input.dispose();
    canvas.remove();
    document.getElementById('match-modals')?.remove();
    renderer.disposeLifecycle(); try { renderer.renderer.dispose?.(); } catch { /* no-op */ }
    delete (window as any).debug;
    unregisterTransientBundle();
  }
}

/** Mount a fresh HomeScreen at a temporarily LOWERED z-index so it sits
 *  behind the live game during the back-to-lobby wipe. Home music is
 *  primed (volume 0, filter reset, play started) but NOT faded in yet —
 *  the caller schedules that fade alongside the visual wipe so audio and
 *  visuals land in sync. */
function prepareReturnHome(): HomeScreen {
  menuAudio.setMusicVolume(0);
  menuAudio.resetFilter();
  menuAudio.playMusic();
  const home = new HomeScreen();
  // Home's default z-index is 100 (above #app, #hud, #match-modals). Lower
  // it here so it stays behind the game layer until the wipe exposes it.
  // Restored by the teardown after the wipe completes.
  home.root.style.zIndex = '5';
  return home;
}

/** Resolve the home the previous runMatch mounted behind the game during
 *  its teardown, or construct a fresh one if we somehow arrived here
 *  without a preload (defensive — first-boot path doesn't use this).
 *  Also exposes the resolved home via a `_lastReturnedHome` slot so the
 *  outer loop can keep a live reference for online play handoff. */
async function showHomeStandalone(): Promise<JoinConfigSummary> {
  const slot = runMatch as unknown as { _pendingReturnHome?: HomeScreen };
  let home = slot._pendingReturnHome;
  if (home) {
    slot._pendingReturnHome = undefined;
  } else {
    // Fallback: no preloaded home (shouldn't happen after first match).
    menuAudio.setMusicVolume(0);
    menuAudio.resetFilter();
    menuAudio.playMusic();
    void menuAudio.fadeMusic(1.0, 800);
    home = new HomeScreen();
    home.activateNav();
  }
  (showHomeStandalone as unknown as { _lastReturnedHome?: HomeScreen })._lastReturnedHome = home;
  // Returning to home is the moment we celebrate any level-ups earned during
  // the just-finished session. Fire-and-forget so the network roundtrip
  // doesn't block the user reaching the menu.
  void home.runLevelUpCheckOnEnter();
  const cfg = await home.show();
  return cfg as JoinConfigSummary;
}

/** Animate a clip-path wipe on each of the passed elements simultaneously.
 *  Mirrors the map-select wipe-out but applied in-place to the live game
 *  layer rather than a black overlay — so what gets revealed is whatever
 *  sits UNDER these elements in the stacking order. */
function wipeGameLayerOut(els: HTMLElement[], durationMs: number): Promise<void> {
  if (els.length === 0) return Promise.resolve();
  for (const el of els) {
    el.style.clipPath = 'inset(0 0 0 0)';
    el.style.transition = `clip-path ${durationMs}ms cubic-bezier(0.65, 0, 0.35, 1)`;
  }
  // Force reflow so the starting clip-path is committed before the target
  // is set — otherwise same-frame style changes can coalesce.
  void els[0]!.offsetHeight;
  for (const el of els) el.style.clipPath = 'inset(0 100% 0 0)';
  return new Promise((resolve) => setTimeout(resolve, durationMs));
}

// ---------------------------------------------------------------------------
// Overlay helpers

/** Fade any DOM-hosted <video>/<audio> elements the game may have produced
 *  (round-end crown SFX plays through an <audio> clone) to silence over
 *  `durationMs`. Menu + game music live on THREE.js AudioContexts and fade
 *  through their own paths — this is strictly the DOM-audio tail. */
function fadeOutDomAudio(durationMs: number): Promise<void> {
  const els = [
    ...Array.from(document.querySelectorAll('video')) as HTMLVideoElement[],
    ...Array.from(document.querySelectorAll('audio')) as HTMLAudioElement[]
  ].filter((el) => el.volume > 0);
  if (els.length === 0) return Promise.resolve();
  const starts = els.map((el) => el.volume);
  const t0 = performance.now();
  return new Promise<void>((resolve) => {
    const step = (): void => {
      const t = Math.min(1, (performance.now() - t0) / durationMs);
      for (let i = 0; i < els.length; i++) els[i]!.volume = starts[i]! * (1 - t);
      if (t >= 1) { resolve(); return; }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  });
}

main().catch((err) => {
  console.error(err);
  const boot = document.getElementById('boot');
  if (boot) boot.innerHTML = `<div style="text-align:center;color:#ff8a8a"><div style="font-size:18px">${t('boot.failed')}</div><div style="opacity:0.7;margin-top:6px;font-size:12px">${(err as Error).message}</div></div>`;
});
